import {
  Business as BusinessIcon,
  Lock as LockIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import {
  Box, Typography, Grid, FormControl, InputLabel,
  Select, MenuItem, TextField
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { BodyUpdateDocumentPermissionsApiDocumentsDocumentIdPermissionsPut } from '../../../api/ApiClient';

type PermissionUpdate = Partial<BodyUpdateDocumentPermissionsApiDocumentsDocumentIdPermissionsPut>;

interface PermissionFormProps {
  permissions: PermissionUpdate;
  onFieldChange: (field: string, value: unknown) => void;
  canModifyPermissions: boolean;
  editMode: boolean;
}

export const PermissionForm: React.FC<PermissionFormProps> = ({
  permissions,
  onFieldChange,
  canModifyPermissions,
  editMode
}) => {
  const { t } = useTranslation();
  if (!editMode || !canModifyPermissions) {
    return null;
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        {t('documents.permissions.editPermissions')}
      </Typography>
      
      <Grid container spacing={2}>
        {/* Visibility Setting */}
        <Grid size={12}>
          <FormControl fullWidth>
            <InputLabel>{t('documents.permissions.visibility')}</InputLabel>
            <Select
              value={permissions.visibility}
              onChange={(e) => onFieldChange('visibility', e.target.value)}
              label={t('documents.permissions.visibility')}
            >
              <MenuItem value="private">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LockIcon sx={{ mr: 1 }} />
                  {t('documents.visibility.private')}
                </Box>
              </MenuItem>
              <MenuItem value="organization">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <BusinessIcon sx={{ mr: 1 }} />
                  {t('documents.visibility.organization')}
                </Box>
              </MenuItem>
              <MenuItem value="public">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PublicIcon sx={{ mr: 1 }} />
                  {t('documents.visibility.public')}
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Shared with Users */}
        <Grid size={12}>
          <TextField
            fullWidth
            label={t('documents.sharing.shareWithUsers')}
            value={permissions.shared_with_users || ''}
            onChange={(e) => onFieldChange('shared_with_users', e.target.value)}
            placeholder={t('documents.sharing.shareWithUsersPlaceholder')}
            helperText={t('documents.sharing.shareWithUsersHelp')}
          />
        </Grid>

        {/* Shared with Groups */}
        <Grid size={12}>
          <TextField
            fullWidth
            label={t('documents.sharing.shareWithGroups')}
            value={permissions.shared_with_groups || ''}
            onChange={(e) => onFieldChange('shared_with_groups', e.target.value)}
            placeholder={t('documents.sharing.shareWithGroupsPlaceholder')}
            helperText={t('documents.sharing.shareWithGroupsHelp')}
          />
        </Grid>
      </Grid>
    </Box>
  );
};